<template>
	<div
		v-if="visible"
		ref="wrap"
		class="customer-service-fab-wrap"
		:style="wrapStyle"
	>
		<transition :name="menuTransitionName">
			<div
				v-if="menuOpen"
				ref="menu"
				class="fab-menu"
				:style="menuStyle"
				@mousedown.stop
				@touchstart.stop
			>
				<div class="fab-menu-item" @click="onCustomerServiceClick">
					<div class="fab-menu-icon">
						<img :src="customerServiceMenuIconSrc" alt="customer service" draggable="false" />
					</div>
					<div class="fab-menu-text">
						<div class="fab-menu-title">Customer Service</div>
						<div class="fab-menu-subtitle">Chat with us</div>
					</div>
				</div>
				<div class="fab-menu-divider" />
				<div class="fab-menu-item" @click="onFeedbackClick">
					<div class="fab-menu-icon">
						<img :src="feedbackMenuIconSrc" alt="feedback" draggable="false" />
					</div>
					<div class="fab-menu-text">
						<div class="fab-menu-title">Feedback</div>
						<div class="fab-menu-subtitle">Send feedback</div>
					</div>
				</div>
			</div>
		</transition>
		<div
			ref="fab"
			class="customer-service-fab"
			@touchstart.prevent="onDragStart"
			@mousedown.prevent="onDragStart"
		>
			<img :src="iconSrc" alt="customer service" draggable="false" />
		</div>
	</div>
</template>

<script>
	import {
		openCustomerService,
		openFeedback,
		resolveImSupportAsync,
		getFeedbackRouteQuery,
		CUSTOMER_SERVICE_DEVICE_ID_UPDATED_EVENT,
	} from '@/utils/customerService';

	const STORAGE_KEY = 'customer_service_fab_position';
	const EDGE_MARGIN_X = 0;
	const EDGE_MARGIN_Y = 8;
	const MENU_OVERLAP_RATIO = 0.18;
	const DRAG_THRESHOLD = 5;
	const FAB_DESIGN_SIZE = 88;
	const MENU_MIN_WIDTH = 440;
	const MENU_HEIGHT_ESTIMATE = 200;

	function estimateRemSize(designPx) {
		if (typeof document === 'undefined') {
			return designPx;
		}
		const fontSize = parseFloat(getComputedStyle(document.documentElement).fontSize) || 50;
		return (designPx / 50) * fontSize;
	}

	function estimateFabSize() {
		const size = estimateRemSize(FAB_DESIGN_SIZE);
		return { w: size, h: size };
	}

	function getContainerBounds() {
		const containerWidth = Math.min(window.innerWidth, 750);
		const offsetLeft = (window.innerWidth - containerWidth) / 2;
		return { containerWidth, offsetLeft };
	}

	function getDefaultPosition(fabSize) {
		const { containerWidth, offsetLeft } = getContainerBounds();
		const { w, h } = fabSize || estimateFabSize();
		return {
			x: offsetLeft + containerWidth - w - EDGE_MARGIN_X,
			y: window.innerHeight * 0.55 - h / 2,
		};
	}

	function getInitialPosition() {
		try {
			const saved = localStorage.getItem(STORAGE_KEY);
			if (saved) {
				return JSON.parse(saved);
			}
		} catch (e) {
			/* ignore */
		}
		if (typeof window === 'undefined') {
			return { x: 0, y: 0 };
		}
		return getDefaultPosition();
	}

	export default {
		name: 'CustomerServiceFab',
		data() {
			const { x, y } = getInitialPosition();
			return {
				visible: false,
				menuOpen: false,
				visibilityRequestId: 0,
				posX: x,
				posY: y,
				menuLayout: {
					side: 'left',
					verticalAlign: 'bottom',
					offsetX: 0,
					offsetY: 0,
				},
				dragging: false,
				dragMoved: false,
				startX: 0,
				startY: 0,
				originX: 0,
				originY: 0,
			};
		},
		computed: {
			iconSrc() {
				return this.$imgBaseUrl + 'customer-service.png';
			},
			customerServiceMenuIconSrc() {
				return this.$imgBaseUrl + 'Feedback/kefu.png';
			},
			feedbackMenuIconSrc() {
				return this.$imgBaseUrl + 'Feedback/fk.png';
			},
			wrapStyle() {
				return {
					left: `${this.posX}px`,
					top: `${this.posY}px`,
				};
			},
			menuStyle() {
				const { side, verticalAlign, offsetX, offsetY } = this.menuLayout;
				const gap = this.getMenuGap();
				const style = {};

				if (side === 'right') {
					style.left = `calc(100% + ${gap}px)`;
					style.right = 'auto';
				} else {
					style.right = `calc(100% + ${gap}px)`;
					style.left = 'auto';
				}

				if (verticalAlign === 'top') {
					style.top = '50%';
					style.bottom = 'auto';
				} else {
					style.bottom = '50%';
					style.top = 'auto';
				}

				if (offsetX || offsetY) {
					style.transform = `translate(${offsetX}px, ${offsetY}px)`;
				}

				return style;
			},
			menuTransitionName() {
				const { side, verticalAlign } = this.menuLayout;
				return `fab-menu-${side}-${verticalAlign}`;
			},
		},
		watch: {
			'$route'() {
				this.closeMenu();
				this.updateVisibility();
			},
			visible(val) {
				if (val) {
					this.$nextTick(() => {
						this.clampPosition();
					});
				} else {
					this.closeMenu();
				}
			},
			menuOpen(val) {
				if (val) {
					this.$nextTick(() => {
						this.updateMenuLayout();
						this.$nextTick(() => {
							this.updateMenuLayout();
							this.bindOutsideClose();
						});
					});
				} else {
					this.unbindOutsideClose();
				}
			},
		},
		mounted() {
			this.updateVisibility();
			this.$nextTick(() => {
				this.clampPosition();
			});
			this.onDeviceIdUpdated = () => this.updateVisibility();
			window.addEventListener(CUSTOMER_SERVICE_DEVICE_ID_UPDATED_EVENT, this.onDeviceIdUpdated);
			window.addEventListener('resize', this.onWindowResize);
			document.addEventListener('touchmove', this.onDragMove, { passive: false });
			document.addEventListener('touchend', this.onDragEnd);
			document.addEventListener('mousemove', this.onDragMove);
			document.addEventListener('mouseup', this.onDragEnd);
		},
		beforeDestroy() {
			this.closeMenu();
			window.removeEventListener(CUSTOMER_SERVICE_DEVICE_ID_UPDATED_EVENT, this.onDeviceIdUpdated);
			window.removeEventListener('resize', this.onWindowResize);
			document.removeEventListener('touchmove', this.onDragMove);
			document.removeEventListener('touchend', this.onDragEnd);
			document.removeEventListener('mousemove', this.onDragMove);
			document.removeEventListener('mouseup', this.onDragEnd);
			this.unbindOutsideClose();
		},
		methods: {
			getContainerBounds,
			getMenuGap() {
				return -this.getFabSize().w * MENU_OVERLAP_RATIO;
			},
			getFabSize() {
				const el = this.$refs.wrap || this.$refs.fab;
				if (el) {
					return { w: el.offsetWidth, h: el.offsetHeight };
				}
				return estimateFabSize();
			},
			getDefaultPosition() {
				return getDefaultPosition(this.getFabSize());
			},
			async updateVisibility() {
				const requestId = ++this.visibilityRequestId;
				const visible = await resolveImSupportAsync(this.$route);
				if (requestId === this.visibilityRequestId) {
					this.visible = visible;
				}
			},
			savePosition() {
				localStorage.setItem(STORAGE_KEY, JSON.stringify({ x: this.posX, y: this.posY }));
			},
			clampPosition() {
				const { containerWidth, offsetLeft } = this.getContainerBounds();
				const { w, h } = this.getFabSize();
				const minX = offsetLeft + EDGE_MARGIN_X;
				const maxX = offsetLeft + containerWidth - w - EDGE_MARGIN_X;
				const maxY = window.innerHeight - h - EDGE_MARGIN_Y;
				this.posX = Math.max(minX, Math.min(this.posX, maxX));
				this.posY = Math.max(EDGE_MARGIN_Y, Math.min(this.posY, maxY));
				if (this.menuOpen) {
					this.updateMenuLayout();
				}
			},
			getMenuHeight() {
				const menuEl = this.$refs.menu;
				return menuEl ? menuEl.offsetHeight : estimateRemSize(MENU_HEIGHT_ESTIMATE);
			},
			getMenuWidth() {
				const menuEl = this.$refs.menu;
				return menuEl ? menuEl.offsetWidth : estimateRemSize(MENU_MIN_WIDTH);
			},
			getMenuRect(side, verticalAlign) {
				const { w: fabW, h: fabH } = this.getFabSize();
				const menuWidth = this.getMenuWidth();
				const menuHeight = this.getMenuHeight();
				const gap = this.getMenuGap();
				let left;
				let top;

				if (side === 'right') {
					left = this.posX + fabW + gap;
				} else {
					left = this.posX - gap - menuWidth;
				}

				const fabCenterY = this.posY + fabH / 2;

				if (verticalAlign === 'top') {
					top = fabCenterY;
				} else {
					top = fabCenterY - menuHeight;
				}

				return { left, top, width: menuWidth, height: menuHeight };
			},
			updateMenuLayout() {
				const { offsetLeft, containerWidth } = this.getContainerBounds();
				const { w: fabW, h: fabH } = this.getFabSize();
				const minLeft = offsetLeft + EDGE_MARGIN_X;
				const maxRight = offsetLeft + containerWidth - EDGE_MARGIN_X;
				const maxBottom = window.innerHeight - EDGE_MARGIN_Y;
				const fabCenterX = this.posX + fabW / 2;
				const fabCenterY = this.posY + fabH / 2;

				const side = fabCenterX < offsetLeft + containerWidth / 2 ? 'right' : 'left';
				let verticalAlign = fabCenterY < window.innerHeight / 2 ? 'top' : 'bottom';

				let rect = this.getMenuRect(side, verticalAlign);

				if (rect.top < EDGE_MARGIN_Y) {
					verticalAlign = 'bottom';
					rect = this.getMenuRect(side, verticalAlign);
				}
				if (rect.top + rect.height > maxBottom) {
					verticalAlign = 'top';
					rect = this.getMenuRect(side, verticalAlign);
				}

				let offsetX = 0;
				let offsetY = 0;

				if (rect.left < minLeft) {
					offsetX = minLeft - rect.left;
				} else if (rect.left + rect.width > maxRight) {
					offsetX = maxRight - (rect.left + rect.width);
				}

				rect = {
					...rect,
					left: rect.left + offsetX,
					top: rect.top,
				};

				if (rect.top < EDGE_MARGIN_Y) {
					offsetY = EDGE_MARGIN_Y - rect.top;
				} else if (rect.top + rect.height > maxBottom) {
					offsetY = maxBottom - (rect.top + rect.height);
				}

				this.menuLayout = { side, verticalAlign, offsetX, offsetY };
			},
			onWindowResize() {
				this.clampPosition();
			},
			getPoint(e) {
				if (e.touches && e.touches.length) {
					return { x: e.touches[0].clientX, y: e.touches[0].clientY };
				}
				return { x: e.clientX, y: e.clientY };
			},
			onDragStart(e) {
				const point = this.getPoint(e);
				this.dragging = true;
				this.dragMoved = false;
				this.startX = point.x;
				this.startY = point.y;
				this.originX = this.posX;
				this.originY = this.posY;
			},
			onDragMove(e) {
				if (!this.dragging) return;
				const point = this.getPoint(e);
				const dx = point.x - this.startX;
				const dy = point.y - this.startY;

				if (!this.dragMoved && Math.abs(dx) + Math.abs(dy) < DRAG_THRESHOLD) {
					return;
				}

				this.dragMoved = true;
				this.closeMenu();
				e.preventDefault();

				this.posX = this.originX + dx;
				this.posY = this.originY + dy;
				this.clampPosition();
			},
			onDragEnd() {
				if (!this.dragging) return;
				this.dragging = false;

				if (this.dragMoved) {
					this.savePosition();
					return;
				}

				setTimeout(() => {
					this.menuOpen = !this.menuOpen;
				}, 0);
			},
			bindOutsideClose() {
				this.unbindOutsideClose();
				document.addEventListener('mousedown', this.onDocumentPointer);
				document.addEventListener('touchstart', this.onDocumentPointer);
			},
			unbindOutsideClose() {
				document.removeEventListener('mousedown', this.onDocumentPointer);
				document.removeEventListener('touchstart', this.onDocumentPointer);
			},
			closeMenu() {
				this.menuOpen = false;
			},
			onDocumentPointer(e) {
				const wrap = this.$refs.wrap;
				if (wrap && wrap.contains(e.target)) {
					return;
				}
				this.closeMenu();
			},
			onCustomerServiceClick() {
				this.closeMenu();
				openCustomerService(this.$route);
			},
			onFeedbackClick() {
				this.closeMenu();
				// 反馈走本仓库 H5 页面；OpenFeedback 仅在有原生 Bridge 时通知 APP
				openFeedback(this.$route);
				this.$router.push({
					path: '/feedback',
					query: getFeedbackRouteQuery(this.$route),
				});
			},
		},
	};
</script>

<style lang="less" scoped>
	.customer-service-fab-wrap {
		position: fixed;
		z-index: 9999;
		width: 88px;
		height: 88px;
		overflow: visible;
	}

	.fab-menu {
		position: absolute;
		z-index: 1;
		width: max-content;
		min-width: 330px;
		background: #fff;
		border-radius: 20px;
		box-shadow: 0 6px 20px rgba(0, 0, 0, 0.14), 0 2px 8px rgba(0, 0, 0, 0.08);
		overflow: hidden;
	}

	.fab-menu-item {
		display: flex;
		align-items: center;
		padding: 20px 24px;
		cursor: pointer;
		-webkit-tap-highlight-color: transparent;

		&:active {
			background: #fafafa;
		}
	}

	.fab-menu-divider {
		height: 1px;
		margin: 0 24px;
		background: #ededed;
	}

	.fab-menu-icon {
		flex-shrink: 0;
		width: 56px;
		height: 56px;
		display: flex;
		align-items: center;
		justify-content: center;
		margin-right: 20px;

		img {
			width: 100%;
			height: 100%;
			display: block;
			object-fit: contain;
			pointer-events: none;
		}
	}

	.fab-menu-text {
		flex: 1;
		min-width: 0;
	}

	.fab-menu-title {
		font-size: 28px;
		font-weight: 700;
		color: #222;
		line-height: 1.3;
		white-space: nowrap;
	}

	.fab-menu-subtitle {
		margin-top: 4px;
		font-size: 22px;
		color: #333;
		line-height: 1.3;
		white-space: nowrap;
	}

	.fab-menu-right-top-enter-active,
	.fab-menu-right-top-leave-active,
	.fab-menu-right-bottom-enter-active,
	.fab-menu-right-bottom-leave-active,
	.fab-menu-left-top-enter-active,
	.fab-menu-left-top-leave-active,
	.fab-menu-left-bottom-enter-active,
	.fab-menu-left-bottom-leave-active {
		transition: opacity 0.16s ease, transform 0.16s ease;
	}

	.fab-menu-right-top-enter,
	.fab-menu-right-top-leave-to,
	.fab-menu-left-top-enter,
	.fab-menu-left-top-leave-to {
		opacity: 0;
		transform: translate(-6px, 6px);
	}

	.fab-menu-right-bottom-enter,
	.fab-menu-right-bottom-leave-to,
	.fab-menu-left-bottom-enter,
	.fab-menu-left-bottom-leave-to {
		opacity: 0;
		transform: translate(-6px, -6px);
	}

	.customer-service-fab {
		position: relative;
		z-index: 2;
		width: 88px;
		height: 88px;
		border-radius: 50%;
		overflow: hidden;
		touch-action: none;
		user-select: none;
		cursor: pointer;
		-webkit-tap-highlight-color: transparent;
		box-shadow: 0 2px 6px rgba(0, 0, 0, 0.14), 0 6px 14px rgba(0, 0, 0, 0.18);
		transform: translateZ(0);
		-webkit-backface-visibility: hidden;
		backface-visibility: hidden;

		img {
			width: 100%;
			height: 100%;
			display: block;
			pointer-events: none;
			border-radius: 50%;
		}
	}
</style>
